import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  absagen,
  akte as ladeAkte,
  durchfuehrungBestaetigen,
  freigeben as freigebenTermin,
  vereinbarungSetzen,
  type Exklusivvereinbarung,
  type Terminakte,
} from '../api/termine';
import { alsEuro, alsMengenangabe } from '../betrag';
import { alsTag } from '../datum';
import { Feld } from '../komponenten/Feld';
import { eine as ladeAnfrage, type Optionsanfrage } from '../api/optionsanfragen';
import { Alternativzeile } from '../angebot/Optionsanfragen';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import { Dialog } from '../komponenten/Dialog';
import { Wartelistenkarte } from './Wartelistenkarte';
import { Kapazitaetskarte } from './Kapazitaetskarte';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';

/**
 * Das Terminfenster — alles zu einem Termin an einer Stelle.
 *
 * ============================================================================
 * DER SATZ, AUS DEM DIESES FENSTER ENTSTAND
 * ============================================================================
 *
 * Auftraggeber, 13.09.2026: „In der terminansicht und trainingsplanung soll
 * jeder Termin auswählbar sein und sich ein popup öffnen, das alle
 * Informationen zum Termin hält, bzw. Rückmeldung zu Optionen oder komplette
 * absagen ermöglicht."
 *
 * ============================================================================
 * EIN FENSTER FÜR DREI MASKEN
 * ============================================================================
 *
 * Terminliste, Kalender und Tagesplanung zeigen denselben Gegenstand aus drei
 * Blickwinkeln — und die Frage „was ist das für ein Termin?" stellt sich in
 * allen dreien gleich. Drei Fenster wären drei Auskünfte, die auseinanderlaufen,
 * sobald eine Angabe dazukommt; hier ist es eines, das an drei Stellen
 * aufgerufen wird.
 *
 * ============================================================================
 * WAS ES KANN, HÄNGT AN DEN RECHTEN (BEN-02)
 * ============================================================================
 *
 *   sehen                          jeder, der Termine sehen darf
 *   termin/absagen                 die Absage mit Grund (ÄA-04, BUC-138)
 *   angebot/terminoption_freigeben die Antwort auf eine Terminoption (ANG-32)
 *   termin/aendern                 Vereinbarung, Webkontingent, Freigabe,
 *                                  Durchführung bestätigen (Tabelle 27, Zeile 9)
 *
 * Wer ein Recht nicht hat, sieht den Abschnitt nicht — und keinen Knopf, der
 * zuverlässig scheitert.
 *
 * ============================================================================
 * DIE ABSAGE IST DAS SCHWERSTE, WAS HIER GEHT
 * ============================================================================
 *
 * Sie schreibt jedem Gebuchten (BUC-138) und gibt die Ressourcen frei. Deshalb
 * steht sie UNTEN, verlangt einen Grund und nennt vorher, wie viele Buchungen
 * betroffen sind. Ein Knopf „Absagen" neben den Stammdaten wäre eine Einladung
 * zum Verklicken.
 */

export function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Der Belegungsstand in Worten (RES-22, BAR-02).
 *
 * Die Datenbank führt ihn ohne Umlaute — `vorlaeufig` ist ein Schlüssel und
 * kein Wort. Auf dem Bildschirm steht das Wort: „ein Ding, ein Name", und
 * Namen tragen hier Umlaute.
 */
const STANDNAME: Readonly<Record<string, string>> = {
  vorlaeufig: 'vorläufig',
  angefragt: 'angefragt',
  zugesagt: 'zugesagt',
  abgesagt: 'abgesagt',
  bestaetigt: 'bestätigt',
};

export function stand(wert: string): string {
  return STANDNAME[wert] ?? wert;
}

export function uhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * BUC-07, CRM-198: der Satz zu den Reservierungen ohne Buchung — leer, wenn
 * es keine gibt. Sie haben keinen Empfänger und laufen ab; einzeln zu
 * benachrichtigen ist da niemand.
 */
export function reservierungssatz(anzahl: number): string {
  if (anzahl === 0) return '';
  return anzahl === 1
    ? 'Dazu 1 Reservierung ohne Buchung – läuft ab, kein Empfänger.'
    : `Dazu ${String(anzahl)} Reservierungen ohne Buchung – laufen ab, kein Empfänger.`;
}

/**
 * Die Meldung nach Verlegen oder Absagen (ÄW-DP-02): Sie nennt, wie viele
 * Kunden angeschrieben wurden — und wie viele nicht. Die Antwort zählt je
 * Buchung; angeschrieben ist, wer eine Nachricht bekam. Reservierungen ohne
 * Buchung zählen nicht als Kunden, sondern stehen in einem eigenen Satz.
 */
export function vorgangsmeldung(
  satz: string,
  angeschrieben: readonly { readonly nachricht: string | null; readonly reservierung?: true }[],
): string {
  const buchungen = angeschrieben.filter((a) => a.reservierung !== true);
  const nachsatz = reservierungssatz(angeschrieben.length - buchungen.length);
  const anhang = nachsatz === '' ? '' : ` ${nachsatz}`;
  if (buchungen.length === 0) return `${satz} Es war keine Buchung betroffen.${anhang}`;
  const erreicht = buchungen.filter((a) => a.nachricht !== null).length;
  const wurde = erreicht === 1 ? 'wurde' : 'wurden';
  return (erreicht === buchungen.length
    ? `${satz} ${String(erreicht)} ${erreicht === 1 ? 'Kunde' : 'Kunden'} ${wurde} angeschrieben.`
    : `${satz} ${String(erreicht)} von ${String(buchungen.length)} Kunden ${wurde} angeschrieben; `
      + 'die übrigen sind einzeln zu benachrichtigen.') + anhang;
}

/**
 * DIE ABSAGE IN DER AKTE (BUC-129, BUC-131, ÄW-DP-02).
 *
 * Grund und Fälle der Absage, mit dem Weg in die Absageliste. Dort werden
 * die Buchungen umgebucht oder erstattet; die Akte sagt, dass es sie gibt.
 * Ohne `beiAbsageliste` bleibt der Verweis Text.
 */
export function Absagehinweis({
  absage,
  beiAbsageliste,
}: {
  readonly absage: NonNullable<Terminakte['absage']>;
  readonly beiAbsageliste?: () => void;
}): JSX.Element {
  const faelle =
    absage.faelle === 0
      ? 'Keine Buchung war betroffen.'
      : `${String(absage.faelle)} ${absage.faelle === 1 ? 'Buchung steht' : 'Buchungen stehen'} als Fall in der Absageliste, ${String(absage.offen)} davon offen.`;
  return (
    <p className="hinweiszeile" role="note">
      Abgesagt am {zeitpunkt(absage.am)} Uhr
      {absage.grund === null ? '.' : <>. Grund: {absage.grund}</>} {faelle}{' '}
      {absage.faelle > 0
        && (beiAbsageliste === undefined ? (
          'Umgebucht oder erstattet wird dort.'
        ) : (
          <Maskenverweis maske={{ art: 'absagen' }} beiKlick={beiAbsageliste}>
            Zur Absageliste
          </Maskenverweis>
        ))}
    </p>
  );
}

/**
 * DIE EXKLUSIVVEREINBARUNG (DAT-08, PRD-46, BUC-68).
 *
 * ============================================================================
 * WARUM SIE HIER STEHT UND NICHT AM BELEG
 * ============================================================================
 *
 * DAT-08: „Die Exklusivvereinbarung ist eine eigene Entität AM TERMIN." Sie
 * gehört nicht dem Angebot, aus dem sie entstanden ist, und nicht dem Auftrag,
 * der sie berechnet — beide zeigen auf sie. Geändert wird sie dort, wo sie
 * hängt.
 *
 * ============================================================================
 * WAS SIE ENTSCHEIDET
 * ============================================================================
 *
 * Sie ist die Preisgrundlage: Nach PRD-46 IST die vereinbarte Pauschale der
 * Rechnungsbetrag — nicht die Teilnehmerzahl, nicht die Preisliste. Fehlt sie,
 * fällt die Preisfindung auf den Listenpreis zurück, und der Terminwähler im
 * Angebot zeigt den Termin gar nicht erst an.
 *
 * ENTWEDER PAUSCHALE ODER STAFFEL. Beides zugleich lässt die Datenbank nicht
 * zu, und keines von beidem wäre eine Vereinbarung ohne Preis.
 */
function Vereinbarungskarte({
  termin,
  vereinbarung,
  kapazitaet,
  kunde,
  darfAendern,
  beiGeaendert,
}: {
  readonly termin: string;
  readonly vereinbarung: Exklusivvereinbarung | null;
  readonly kapazitaet: number;
  readonly kunde: string | null;
  readonly darfAendern: boolean;
  readonly beiGeaendert: () => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const [art, setzeArt] = useState<'pauschale' | 'staffel'>(
    vereinbarung?.staffelnd === true ? 'staffel' : 'pauschale',
  );
  const [menge, setzeMenge] = useState(String(vereinbarung?.vereinbarteKapazitaet ?? kapazitaet));
  const [betrag, setzeBetrag] = useState(vereinbarung?.pauschale ?? '');
  const [brutto, setzeBrutto] = useState(vereinbarung?.istBrutto ?? false);
  const [frist, setzeFrist] = useState(vereinbarung?.meldefrist?.slice(0, 10) ?? '');
  const [regel, setzeRegel] = useState<'zusatzleistung' | 'abweisen'>(
    vereinbarung?.ueberschreitungsregel ?? 'zusatzleistung',
  );
  const [hinweis, setzeHinweis] = useState(vereinbarung?.ueberschreitungshinweis ?? '');
  const [bemerkung, setzeBemerkung] = useState(vereinbarung?.bemerkung ?? '');
  const [stufen, setzeStufen] = useState<readonly { ab: string; preis: string }[]>(
    vereinbarung?.staffel.map((s) => ({ ab: String(s.abKapazitaet), preis: s.pauschale })) ?? [
      { ab: '1', preis: '' },
    ],
  );

  /*
   * ÄW-DP-04, DAT-08: Die Vereinbarung gehört EINEM Kunden. Hat der Termin
   * keinen (kein Herkunftsbeleg, keine Vereinbarung), gäbe es nichts zu
   * speichern — die Karte sagt das, statt am Server zu scheitern.
   */
  const kundeDerVereinbarung = vereinbarung?.kunde ?? kunde;
  const ohneKunde = kundeDerVereinbarung === null;

  const speichern = (): void => {
    if (kundeDerVereinbarung === null) return;
    setzeLaeuft(true);
    void vereinbarungSetzen(termin, {
      kunde: kundeDerVereinbarung,
      vereinbarteKapazitaet: Number(menge),
      pauschale: art === 'pauschale' ? betrag : null,
      istBrutto: brutto,
      meldefrist: frist === '' ? null : `${frist}T12:00:00.000Z`,
      ueberschreitungsregel: regel,
      ueberschreitungshinweis: hinweis === '' ? null : hinweis,
      bemerkung: bemerkung === '' ? null : bemerkung,
      staffel:
        art === 'staffel'
          ? stufen
              .filter((s) => s.ab !== '' && s.preis !== '')
              .map((s) => ({ abKapazitaet: Number(s.ab), pauschale: s.preis }))
          : [],
    })
      .then(() => {
        setzeOffen(false);
        setzeFehler(null);
        beiGeaendert();
      })
      .catch((u: unknown) => setzeFehler(u as ApiFehler))
      .finally(() => setzeLaeuft(false));
  };

  return (
    <>
      <h3>Exklusivvereinbarung</h3>

      {vereinbarung === null ? (
        <p className="hilfe">
          Für diesen Termin ist nichts vereinbart. Ohne Vereinbarung gilt der Listenpreis, und
          der Termin steht im Angebot nicht zur Auswahl.
        </p>
      ) : (
        <dl className="angaben">
          <dt>Kunde</dt>
          <dd>{vereinbarung.kundenname}</dd>
          <dt>Vereinbarte Kapazität</dt>
          <dd>
            {alsMengenangabe(vereinbarung.vereinbarteKapazitaet, 'Platz')}
            <span className="hilfe"> · dem Kunden ganz zugeordnet</span>
          </dd>
          <dt>Preis</dt>
          <dd>
            {vereinbarung.preis === null ? (
              <span className="marke marke--krit">
                kein Preis — die Staffel deckt die Kapazität nicht ab
              </span>
            ) : (
              <>
                <strong>{alsEuro(vereinbarung.preis)}</strong>{' '}
                {vereinbarung.istBrutto ? 'brutto' : 'netto'}
                {vereinbarung.staffelnd && <span className="hilfe"> · aus der Staffel</span>}
                {vereinbarung.preisFestgeschrieben && (
                  <span className="marke marke--inaktiv">auf einem Beleg festgeschrieben</span>
                )}
              </>
            )}
          </dd>
          {vereinbarung.staffelnd && vereinbarung.staffel.length > 0 && (
            <>
              <dt>Staffel</dt>
              <dd>
                <ul className="optionen__stand">
                  {vereinbarung.staffel.map((s) => (
                    <li key={s.abKapazitaet}>
                      ab {alsMengenangabe(s.abKapazitaet, 'Platz')}: {alsEuro(s.pauschale)}
                    </li>
                  ))}
                </ul>
              </dd>
            </>
          )}
          <dt>Meldefrist</dt>
          <dd>
            {/* DAT-08; ÄA-41: Eine Frist steht in der Achtungsfarbe. */}
            {vereinbarung.meldefrist === null ? (
              'keine vereinbart'
            ) : (
              <span className="marke marke--warn">
                Teilnehmerdaten bis {String(alsTag(vereinbarung.meldefrist))}
              </span>
            )}
          </dd>
          <dt>Bei Überschreitung</dt>
          <dd>
            {vereinbarung.ueberschreitungsregel === 'abweisen'
              ? 'zusätzliche Teilnehmer werden abgewiesen'
              : 'zusätzliche Teilnehmer als Zusatzleistung berechnen'}
            {vereinbarung.ueberschreitungshinweis !== null && (
              <span className="hilfe"> · {vereinbarung.ueberschreitungshinweis}</span>
            )}
          </dd>
          {vereinbarung.bemerkung !== null && (
            <>
              <dt>Bemerkung</dt>
              <dd>{vereinbarung.bemerkung}</dd>
            </>
          )}
        </dl>
      )}

      {fehler !== null && <Meldung fehler={fehler} />}

      {darfAendern && ohneKunde && (
        <p className="hilfe">Dem Termin ist kein Kunde zugeordnet.</p>
      )}

      {darfAendern && !ohneKunde && !offen && (
        <button type="button" className="knopf" onClick={() => setzeOffen(true)}>
          {vereinbarung === null ? 'Vereinbarung anlegen' : 'Vereinbarung ändern'}
        </button>
      )}

      {darfAendern && !ohneKunde && offen && (
        <form
          className="unterblock"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            speichern();
          }}
        >
          {/* PRF-16 */}
          {vereinbarung?.preisFestgeschrieben === true && (
            <p className="warnung">
              Der Preis steht auf einem Beleg, den der Kunde hat, und ändert sich nicht mehr.
              Weicht der Umfang ab, ist das eine <strong>neue</strong> Vereinbarung
              — nicht eine geänderte. Meldefrist, Regel und Bemerkung lassen sich weiterhin
              pflegen.
            </p>
          )}

          <Feld
            beschriftung="Vereinbarte Kapazität"
            wert={menge}
            aendern={setzeMenge}
            hilfe="Die Plätze, die dem Kunden zugeordnet sind — nicht die später gemeldete Teilnehmerzahl."
          />

          <div className="feld">
            <span className="feld__beschriftung" id="ev-art">
              Preisform
            </span>
            <div className="beginnwahl" role="group" aria-labelledby="ev-art">
              <label className="liste__schalter">
                <input
                  type="radio"
                  name="ev-art"
                  checked={art === 'pauschale'}
                  onChange={() => setzeArt('pauschale')}
                />
                feste Pauschale
              </label>
              <label className="liste__schalter">
                <input
                  type="radio"
                  name="ev-art"
                  checked={art === 'staffel'}
                  onChange={() => setzeArt('staffel')}
                />
                Staffel nach Kapazität
              </label>
            </div>
            <p className="feld__hilfe">
              Eines von beidem. Die Staffel gilt „ab" — die höchste Stufe, welche die
              vereinbarte Kapazität noch erreicht, ist der Preis.
            </p>
          </div>

          {art === 'pauschale' ? (
            <Feld
              beschriftung="Pauschale"
              wert={betrag}
              aendern={setzeBetrag}
              hilfe="Der Rechnungsbetrag des Termins. Punkt als Dezimaltrenner, etwa 3200.00."
            />
          ) : (
            <div className="feld">
              <span className="feld__beschriftung">Stufen</span>
              {/*
                DER INDEX ALS SCHLUESSEL, UND DAS IST HIER RICHTIG: Die Stufen
                haben keine eigene Kennung — sie entstehen und vergehen mit der
                Staffel, und ihre REIHENFOLGE ist die Ordnung, die der Anwender
                sieht. Ein zusammengesetzter Schluessel aus „ab" und Betrag
                verlöre beim Tippen den Fokus.
              */}
              {stufen.map((s, i) => (
                <div className="kanalzeile" key={i}>
                  <input
                    className="feld__eingabe feld__eingabe--schmal"
                    aria-label={`Stufe ${String(i + 1)} — ab Kapazität`}
                    value={s.ab}
                    onChange={(e) =>
                      setzeStufen(
                        stufen.map((x, j) => (i === j ? { ...x, ab: e.target.value } : x)),
                      )
                    }
                  />
                  <input
                    className="feld__eingabe"
                    aria-label={`Stufe ${String(i + 1)} — Pauschale`}
                    value={s.preis}
                    onChange={(e) =>
                      setzeStufen(
                        stufen.map((x, j) => (i === j ? { ...x, preis: e.target.value } : x)),
                      )
                    }
                  />
                  {/* Ohne Rückfrage (CRM-104): Die Zeile gilt erst mit „Speichern". */}
                  <button
                    type="button"
                    className="knopf knopf--eingriff knopf--klein"
                    onClick={() => setzeStufen(stufen.filter((_, j) => j !== i))}
                  >
                    Entfernen
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => setzeStufen([...stufen, { ab: '', preis: '' }])}
              >
                Stufe hinzufügen
              </button>
            </div>
          )}

          <label className="liste__schalter">
            <input type="checkbox" checked={brutto} onChange={(e) => setzeBrutto(e.target.checked)} />
            Der Betrag enthält die Umsatzsteuer
          </label>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ev-frist">
              Meldefrist für Teilnehmerdaten
            </label>
            <input
              id="ev-frist"
              type="date"
              className="feld__eingabe"
              value={frist}
              onChange={(e) => setzeFrist(e.target.value)}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ev-regel">
              Bei Überschreitung
            </label>
            <select
              id="ev-regel"
              className="feld__eingabe"
              value={regel}
              onChange={(e) => setzeRegel(e.target.value as 'zusatzleistung' | 'abweisen')}
            >
              <option value="zusatzleistung">als Zusatzleistung berechnen</option>
              <option value="abweisen">abweisen</option>
            </select>
          </div>

          <Feld beschriftung="Hinweis zur Überschreitung" wert={hinweis} aendern={setzeHinweis} />
          <Feld beschriftung="Bemerkung" wert={bemerkung} aendern={setzeBemerkung} />

          <div className="knopfreihe knopfreihe--abschluss">
            <button type="button" className="knopf" onClick={() => setzeOffen(false)}>
              Abbrechen
            </button>
            <button type="submit" className="knopf knopf--haupt" disabled={laeuft}>
              Speichern
            </button>
          </div>
        </form>
      )}
    </>
  );
}

export function Terminfenster({
  termin,
  darfAbsagen,
  darfOptionBeantworten,
  darfVereinbarungAendern = false,
  darfWartelisteSehen = false,
  beiSchliessen,
  beiAenderung,
  beiAngebot,
  beiAkte,
}: {
  readonly termin: string;
  readonly darfAbsagen: boolean;
  readonly darfOptionBeantworten: boolean;
  /** BUC-11 (ÄA-55): wie viele Wartende zum Termin passen — `buchung/sehen`. */
  readonly darfWartelisteSehen?: boolean;
  /** DAT-08: `termin/aendern` — die Vereinbarung hängt am Termin. */
  readonly darfVereinbarungAendern?: boolean;
  readonly beiSchliessen: () => void;
  /** Nach Absage oder Optionsantwort: die Maske dahinter lädt neu. */
  readonly beiAenderung: () => void;
  /** Der Weg ins Angebot — `undefined`, wo die Maske ihn nicht anbietet. */
  readonly beiAngebot?: (angebot: string) => void;
  /** CRM-117 (L6): der Weg in die Terminakte — `undefined`, wo die Maske ihn nicht anbietet. */
  readonly beiAkte?: (termin: string) => void;
}): JSX.Element {
  const [akte, setzeAkte] = useState<Terminakte | null>(null);
  const [anfrage, setzeAnfrage] = useState<Optionsanfrage | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [absageOffen, setzeAbsageOffen] = useState(false);
  const [durchfuehrungOffen, setzeDurchfuehrungOffen] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  const holen = useCallback(async () => {
    try {
      const geladen = await ladeAkte(termin);
      setzeAkte(geladen);
      setzeFehler(null);
      /*
       * Die Option kommt über ihren eigenen Weg. Das ist keine zweite Runde
       * aus Bequemlichkeit: Der Vorgang gehört dem Angebot, und seine Auskunft
       * soll an EINER Stelle entstehen (ANG-30).
       */
      if (geladen.optionsanfrage !== null) {
        try {
          setzeAnfrage(await ladeAnfrage(geladen.optionsanfrage));
        } catch (e) {
          /* Wer die Option nicht sehen darf, sieht den Rest trotzdem. */
          if (!(e instanceof ApiFehler)) throw e;
          setzeAnfrage(null);
        }
      } else {
        setzeAnfrage(null);
      }
    } catch (e) {
      if (!(e instanceof ApiFehler)) throw e;
      setzeFehler(e);
    }
  }, [termin]);

  useEffect(() => {
    void holen();
  }, [holen]);

  /**
   * Die Bestätigung der Durchführung aus der Rückfrage (Tabelle 27, Zeile 9;
   * Auftrag CRM-111). Wirft wie die Absage: Der Fehler steht im Pop-up.
   */
  async function bestaetigeDurchfuehrung(): Promise<void> {
    setzeLaeuft(true);
    try {
      await durchfuehrungBestaetigen(termin);
      setzeMeldung('Die Durchführung ist bestätigt.');
      setzeDurchfuehrungOffen(false);
      await holen();
      beiAenderung();
    } finally {
      setzeLaeuft(false);
    }
  }

  /**
   * Die Absage aus der Rückfrage (CRM-104). Wirft: Der Fehler steht im
   * Pop-up, und es bleibt offen; erst der Erfolg schließt es.
   */
  async function sageAb(grund: string): Promise<void> {
    setzeLaeuft(true);
    try {
      const ergebnis = await absagen(termin, grund);
      setzeMeldung(vorgangsmeldung('Der Termin ist abgesagt.', ergebnis.angeschrieben));
      setzeAbsageOffen(false);
      await holen();
      beiAenderung();
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog
      titel={akte === null ? 'Termin' : akte.produkt}
      unterzeile={akte === null ? undefined : `${zeitpunkt(akte.beginn)}–${uhrzeit(akte.ende)} Uhr`}
      breite="breit"
      beiSchliessen={beiSchliessen}
    >
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      {akte === null ? (
        <p className="hilfe">Wird geladen …</p>
      ) : (
        <>
          {beiAkte !== undefined && (
            <p>
              <Maskenverweis
                maske={{ art: 'terminakte', id: akte.id }}
                beiKlick={() => {
                  beiAkte(akte.id);
                }}
              >
                Terminakte öffnen
              </Maskenverweis>
            </p>
          )}
          {/* BUC-129, BUC-131: Grund und Fälle; der Verweis steht in der Terminakte. */}
          {akte.absage !== null && <Absagehinweis absage={akte.absage} />}
          <dl className="angaben">
            <dt>Produkt</dt>
            <dd>
              {akte.produkt} <span className="hilfe">{akte.artikelnummer}</span>
            </dd>
            <dt>Zeit</dt>
            <dd>
              {zeitpunkt(akte.beginn)} bis {uhrzeit(akte.ende)} Uhr
            </dd>
            <dt>Ort</dt>
            <dd>{akte.ort ?? 'kein Standort hinterlegt'}</dd>
            <dt>Art</dt>
            <dd>
              {akte.terminart === 'exklusiv' ? 'Exklusivtermin' : 'offener Termin'}{' '}
              <span
                className={
                  akte.status === 'abgesagt'
                    ? 'marke marke--krit'
                    : akte.status === 'buchbar'
                      ? 'marke marke--gut'
                      : 'marke marke--inaktiv'
                }
              >
                {/* Der Schlüssel ohne Umlaut ist kein Wort; auf dem Bildschirm steht das Wort. */}
                {akte.status === 'durchgefuehrt' ? 'durchgeführt' : akte.status}
              </span>
              {/* ANG-30: Ein Termin aus einem Wunsch ist noch keine Zusage. */}
              {akte.optionsanfrage !== null && (
                <span className="marke marke--warn">Option</span>
              )}
            </dd>
            <dt>Plätze</dt>
            <dd>
              {/*
                KAP-22: die ANONYME Sicht. Die maßgebliche freie Kapazität hängt
                am Anfragenden (KAP-04) und steht in der Buchungsstrecke — hier
                geht es um den Überblick, nicht um die Zusage.
              */}
              {akte.terminart === 'exklusiv'
                ? `${String(akte.hoechstteilnehmer)} (dem Kunden ganz zugeordnet)`
                : `${String(akte.belegt)} von ${String(akte.hoechstteilnehmer)} vergeben`}
              {akte.mindestteilnehmer > 0
                && `, Mindestzahl ${String(akte.mindestteilnehmer)}`}
            </dd>
            <dt>Buchungen</dt>
            <dd>
              {akte.buchungen === 0
                ? 'keine'
                : `${String(akte.buchungen)} mit ${String(akte.gebuchtePlaetze)} Plätzen`}
            </dd>
            {akte.kundenname !== null && (
              <>
                <dt>Kunde</dt>
                <dd>{akte.kundenname}</dd>
              </>
            )}
            {(akte.angebotsnummer !== null || akte.auftragsnummer !== null) && (
              <>
                <dt>Herkunft</dt>
                <dd>
                  {akte.angebotsnummer !== null
                    && (beiAngebot === undefined || akte.herkunftAngebot === null ? (
                      <>Angebot {akte.angebotsnummer}</>
                    ) : (
                      <button
                        type="button"
                        className="knopf knopf--verweis"
                        onClick={() => {
                          beiAngebot(akte.herkunftAngebot!);
                        }}
                      >
                        Angebot {akte.angebotsnummer}
                      </button>
                    ))}
                  {akte.auftragsnummer !== null && <> Auftrag {akte.auftragsnummer}</>}
                </dd>
              </>
            )}
            <dt>Betriebstag</dt>
            <dd>{akte.betriebstagFreigegeben ? 'freigegeben' : 'in Planung'}</dd>
          </dl>

          {/*
            KAP-20, BUC-71: die Zusammensetzung der freien Kapazität und das
            Webkontingent. `termin/aendern` wie die Vereinbarung — beides sind
            Angaben am Termin (BEN-02).
          */}
          <Kapazitaetskarte
            key={JSON.stringify(akte.kapazitaet.wege)}
            termin={akte.id}
            terminart={akte.terminart}
            kapazitaet={akte.kapazitaet}
            darfAendern={darfVereinbarungAendern}
            beiGeaendert={() => void holen()}
          />

          {/* TAG-15, RES-22: wer und was am Termin hängt — vorgemerkt wie belegt. */}
          <h3>Ressourcen</h3>
          {akte.ressourcen.length === 0 ? (
            <p className="hilfe">Für diesen Termin ist noch nichts zugeteilt.</p>
          ) : (
            <ul className="optionen__stand">
              {akte.ressourcen.map((r) => (
                <li key={r.belegung}>
                  {r.bezeichnung} <span className="hilfe">{r.kennung} · {r.art}</span>{' '}
                  <span className={r.hart ? 'marke marke--gut' : 'marke'}>
                    {r.hart ? stand(r.status) : `${stand(r.status)}, vorgemerkt`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* DAT-08: die Preisgrundlage des Exklusivtermins. */}
          {akte.terminart === 'exklusiv' && (
            <Vereinbarungskarte
              termin={akte.id}
              vereinbarung={akte.vereinbarung}
              kapazitaet={akte.hoechstteilnehmer}
              /* ÄW-DP-04, BUC-68: Die erste Vereinbarung nimmt den Kunden des Herkunftsbelegs. */
              kunde={akte.vereinbarung?.kunde ?? akte.kunde}
              darfAendern={darfVereinbarungAendern}
              beiGeaendert={() => void holen()}
            />
          )}

          {/*
            DIE WARTELISTE (BUC-11 nach ÄA-55).

            Nur bei einem Produkt, das eine führt — und nur beim offenen
            Termin: Ein Exklusivtermin gehört EINEM Kunden (BUC-66), auf ihn
            wartet niemand. Die Zahl sieht, wer Buchungen sieht.
          */}
          {akte.warteliste && akte.terminart === 'offen' && akte.status !== 'abgesagt' && darfWartelisteSehen && (
            <Wartelistenkarte termin={akte.id} />
          )}

          {/*
            DIE ANTWORT AUF DIE TERMINOPTION (ANG-32).

            Dasselbe Bauteil wie in der Arbeitsliste der Disposition — eine
            Antwort, ein Weg. Wer den Termin im Kalender vor sich hat, soll
            nicht erst in eine andere Maske wechseln müssen.
          */}
          {anfrage !== null && darfOptionBeantworten && (
            <>
              <h3>Terminoption · {anfrage.angebotsnummer}</h3>
              <p className="hilfe">
                {anfrage.leistung} für {anfrage.teilnehmerzahl} Teilnehmer, angefragt von{' '}
                {anfrage.anfragendername}.
              </p>
              <ul className="optionen__alternativen">
                {anfrage.alternativen.map((a) => (
                  <Alternativzeile
                    key={a.id}
                    anfrage={anfrage}
                    alternative={a}
                    beiAntwort={async () => {
                      await holen();
                      beiAenderung();
                    }}
                  />
                ))}
              </ul>
            </>
          )}
          {anfrage !== null && !darfOptionBeantworten && (
            <p className="hilfe">
              Dieser Termin hält eine Terminoption aus Angebot {anfrage.angebotsnummer}. Die
              Antwort gibt die Disposition.
            </p>
          )}

          {/*
            DIE ABSAGE STEHT UNTEN UND VERLANGT EINEN GRUND (ÄA-04, BUC-138).

            Sie schreibt jedem Gebuchten und gibt die Ressourcen frei. Ein Knopf
            neben den Stammdaten wäre eine Einladung zum Verklicken; hier steht
            er hinter einem zweiten Griff und nennt vorher, wen es trifft.
          */}
          {/*
            TABELLE 27, ZEILE 2: DIE VERKAUFSFREIGABE (0252). Ein geplanter Termin
            ist fuer Portal und Buchung unsichtbar; hier wird er sichtbar.
          */}
          {darfVereinbarungAendern && akte.status === 'geplant' && (
            <div className="terminfenster__absage">
              <h3>Verkauf</h3>
              <p className="hilfe">
                Der Termin ist geplant, aber noch nicht buchbar. Erst mit der Freigabe erscheint er
                im Portal und in der Buchungsauswahl.
              </p>
              <button
                type="button"
                className="knopf knopf--klein knopf--haupt"
                disabled={laeuft}
                onClick={() => {
                  setzeLaeuft(true);
                  freigebenTermin(termin)
                    .then(async ({ freigegeben }) => {
                      setzeMeldung(
                        freigegeben
                          ? 'Zum Verkauf freigegeben.'
                          : 'Der Termin war nicht mehr „geplant".',
                      );
                      await holen();
                      beiAenderung();
                    })
                    .catch((f: unknown) => {
                      setzeFehler(f instanceof ApiFehler ? f : null);
                    })
                    .finally(() => {
                      setzeLaeuft(false);
                    });
                }}
              >
                Zum Verkauf freigeben
              </button>
            </div>
          )}
          {/*
            TABELLE 27, ZEILE 9: DIE DURCHFÜHRUNG BESTÄTIGEN (BUC-05, CRM-111).

            Nach dem Terminende, aus „buchbar" oder „ausgebucht". Kein Lauf
            stellt das nach der Uhr um (STA-01) — es bestätigt die Orga. Ein
            einziger Griff mit Rückfrage, deshalb auch auf dem Telefon (ÄA-63).
          */}
          {darfVereinbarungAendern
            && (akte.status === 'buchbar' || akte.status === 'ausgebucht')
            && new Date(akte.ende).getTime() <= Date.now() && (
            <div className="terminfenster__absage">
              <h3>Durchführung</h3>
              <p className="hilfe">
                Der Termin ist vorbei. Mit der Bestätigung beginnt die Nachbereitung; Stornos
                sind danach nicht mehr möglich.
              </p>
              <button
                type="button"
                className="knopf knopf--klein knopf--haupt"
                disabled={laeuft}
                onClick={() => {
                  setzeDurchfuehrungOffen(true);
                }}
              >
                Durchführung bestätigen …
              </button>
            </div>
          )}
          {darfAbsagen && akte.status !== 'abgesagt' && akte.status !== 'durchgefuehrt' && (
            <div className="terminfenster__absage">
              <h3>Termin absagen</h3>
              <button
                type="button"
                className="knopf knopf--eingriff knopf--klein"
                disabled={laeuft}
                onClick={() => {
                  setzeAbsageOffen(true);
                }}
              >
                Absagen …
              </button>
            </div>
          )}
        </>
      )}

      {/*
        DIE ABSAGE FRAGT IM POP-UP NACH (CRM-104). Der Grund steht nur dort —
        die aufgeklappte Warnung mit eigenem Feld, die hier vorher stand, wäre
        neben der Rückfrage eine zweite Bestätigung derselben Tat.
      */}
      {durchfuehrungOffen && akte !== null && (
        <Rueckfrage
          frage="Durchführung bestätigen?"
          unterzeile={`${akte.produkt} · ${zeitpunkt(akte.beginn)} Uhr`}
          tat="Bestätigen"
          beiBestaetigen={bestaetigeDurchfuehrung}
          beiSchliessen={() => {
            setzeDurchfuehrungOffen(false);
          }}
        >
          <p>
            {akte.gebuchtePlaetze === 0
              ? 'Auf diesem Termin liegt keine Buchung.'
              : `Anwesenheit erfasst für ${String(akte.anwesenheitErfasst)} von ${String(akte.gebuchtePlaetze)} Plätzen.`}{' '}
            Der Termin gilt dann als durchgeführt. Das lässt sich nicht zurücknehmen; Stornos
            sind danach nicht mehr möglich, Nacherfassungen schon.
          </p>
        </Rueckfrage>
      )}

      {absageOffen && akte !== null && (
        <Rueckfrage
          frage="Termin absagen?"
          unterzeile={`${akte.produkt} · ${zeitpunkt(akte.beginn)} Uhr`}
          tat="Absagen"
          grund={{
            beschriftung: 'Grund der Absage',
            hilfe: 'Der Grund geht an die Gebuchten und steht im Protokoll.',
          }}
          beiBestaetigen={sageAb}
          beiSchliessen={() => {
            setzeAbsageOffen(false);
          }}
        >
          <p>
            {akte.buchungen === 0
              ? 'Auf diesem Termin liegt keine Buchung.'
              : `${String(akte.buchungen)} ${akte.buchungen === 1 ? 'Buchung' : 'Buchungen'} mit ${String(akte.gebuchtePlaetze)} Plätzen werden angeschrieben.`}{' '}
            Die Kunden werden benachrichtigt; ihre Buchungen erscheinen als Fälle in der
            Absageliste (Buchung → Absagen) und werden dort umgebucht oder erstattet. Die
            Einsätze am Termin werden abgesagt. Die Absage lässt sich nicht zurücknehmen.
          </p>
        </Rueckfrage>
      )}
    </Dialog>
  );
}
